import { useMemo } from 'react';
import { LogEntryWithType, FormGroup } from '@/types/TInbox';
import moment from 'moment';

import { isConversationEmpty } from 'src/utils/inboxUtils';

export const useTabCounts = (
	allLogs: LogEntryWithType[],
	formGroups: FormGroup[],
	activeTab: string,
	searchTerm: string,
	dateRange: [any, any] | null,
	statusFilter: string,
	durationFilter: string,
	hideEmptyConvos: boolean,
	isLegacyIntakeFormsEnabled: boolean,
) => {
	let totalCallCount = 0;
	const filteredLogs = useMemo(() => {
		// For Intake Forms tab, don't show any logs in sidebar
		if (activeTab === 'Intake Forms') {
			return [];
		}

		let logs = allLogs;

		if (hideEmptyConvos) {
			logs = logs.filter((log) => !isConversationEmpty(log));
		}

		return logs.filter((log) => {
			// Apply search filter
			const matchesSearch =
				searchTerm === '' ||
				(log.contact.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
				log.message.toLowerCase().includes(searchTerm.toLowerCase());

			// Apply date filter
			let matchesDate = true;
			if (dateRange && dateRange[0] && dateRange[1]) {
				const logDate = moment(log.date);
				matchesDate = logDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
			}

			// Apply status filter
			const matchesStatus = statusFilter === 'all' || log.status === statusFilter;

			// Apply duration filter
			let matchesDuration = true;
			if (durationFilter !== 'all' && log.type === 'call' && log.duration) {
				const duration = parseInt(log.duration);
				if (durationFilter === 'short') {
					matchesDuration = duration < 60;
				} else if (durationFilter === 'medium') {
					matchesDuration = duration >= 60 && duration <= 300;
				} else if (durationFilter === 'long') {
					matchesDuration = duration > 300;
				}
			}

			// Apply tab filter
			let matchesTab = true;
			if (activeTab === 'Calls') {
				matchesTab = log.type === 'call';
			} else if (activeTab === 'Texts') {
				matchesTab = log.type === 'text';
			} else if (activeTab === 'Voicemails') {
				matchesTab = log.type === 'voicemail';
			}
			// For 'All Inbox', show all logs

			return matchesSearch && matchesDate && matchesStatus && matchesDuration && matchesTab;
		});
	}, [allLogs, activeTab, searchTerm, dateRange, statusFilter, durationFilter, hideEmptyConvos]);

	const tabCounts = useMemo(() => {
		// Calculate base counts without tab filter (but with other filters)
		const baseFilteredLogs = allLogs.filter((log) => {
			const matchesSearch =
				searchTerm === '' ||
				(log.contact.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
				log.message.toLowerCase().includes(searchTerm.toLowerCase());

			let matchesDate = true;
			if (dateRange && dateRange[0] && dateRange[1]) {
				const logDate = moment(log.date);
				matchesDate = logDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
			}

			const matchesStatus = statusFilter === 'all' || log.status === statusFilter;

			let matchesDuration = true;
			if (durationFilter !== 'all' && log.type === 'call' && log.duration) {
				const duration = parseInt(log.duration);
				if (durationFilter === 'short') {
					matchesDuration = duration < 60;
				} else if (durationFilter === 'medium') {
					matchesDuration = duration >= 60 && duration <= 300;
				} else if (durationFilter === 'long') {
					matchesDuration = duration > 300;
				}
			}

			const matchesHideEmpty = !hideEmptyConvos || !isConversationEmpty(log);

			return matchesSearch && matchesDate && matchesStatus && matchesDuration && matchesHideEmpty;
		});

		totalCallCount = allLogs.filter((log) => log.type === 'call').length;
		const callsCount = baseFilteredLogs.filter((log) => log.type === 'call').length;
		const textsCount = baseFilteredLogs.filter((log) => log.type === 'text').length;
		const voicemailsCount = baseFilteredLogs.filter((log) => log.type === 'voicemail').length;
		const formsCount = formGroups.length; // Forms count is separate

		const counts = {
			all: baseFilteredLogs.length, // All regular logs (no forms)
			totalCallCount,
			calls: callsCount,
			texts: textsCount,
			voicemails: voicemailsCount,
			forms: 0,
		};

		if (isLegacyIntakeFormsEnabled) {
			counts.forms = formsCount;
		}

		return counts;
	}, [allLogs, formGroups, searchTerm, dateRange, statusFilter, durationFilter, hideEmptyConvos]);

	return { filteredLogs, tabCounts, totalCallCount };
};
